import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// CRM lead pipeline — embedded inline on the Resources page. A kanban board
// (columns = admin-configurable stages), a KPI strip whose numbers feed the
// OKR rollup, plus lead create/edit, stage moves, the "Start template flow"
// action (spins up a project from a template and links it back), and a
// curator-only stage manager. Collapsed by default so it doesn't crowd the
// Resources page; the header always shows the open-lead + signed counts.
import { notify } from "../Toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import MultiSelect from "../MultiSelect";
import { useAuth } from "../../hooks/useAuth";
import { useOrgCatalog } from "../../hooks/useOrgCatalog";
const LS_OPEN = "crm-board-open";
function money(n) {
    if (n == null)
        return "";
    return "$" + n.toLocaleString();
}
// Short local date for follow-up display, e.g. "Thu, May 28".
function fmtFollowUp(iso) {
    if (!iso)
        return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return "";
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}
// Facts that came from ArtistBeat, shown with ArtistBeat's own labels so both
// apps use the same terms (Writer IPI, Rights Society, Deal status, Est. annual).
function ArtistBeatTags({ data }) {
    if (!data)
        return null;
    const deal = data.deal_status === "in_deal"
        ? "In publishing deal"
        : data.deal_status === "independent"
            ? "Independent"
            : null;
    const range = data.est_low_annual != null && data.est_high_annual != null
        ? `${money(data.est_low_annual)}–${money(data.est_high_annual)}/yr`
        : data.est_current_annual != null
            ? `${money(data.est_current_annual)}/yr`
            : null;
    return (_jsxs(_Fragment, { children: [data.writer_ipi && _jsxs("span", { className: "crm-tag", title: "Writer IPI", children: ["IPI ", data.writer_ipi] }), data.pro && _jsx("span", { className: "crm-tag", title: "Rights Society", children: data.pro }), deal && _jsx("span", { className: "crm-tag", title: "Deal status", children: deal }), range && _jsx("span", { className: "crm-tag", title: "Est. annual royalties (ArtistBeat audit)", children: range }), data.report_url && (_jsx("a", { className: "crm-tag", href: data.report_url, target: "_blank", rel: "noreferrer", onClick: (e) => e.stopPropagation(), children: "ArtistBeat audit \u2197" }))] }));
}
function isOverdue(iso) {
    if (!iso)
        return false;
    const d = new Date(iso);
    return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}
// Build + download a CSV of the given leads (the currently-filtered set).
function exportLeadsCsv(leads, stageName) {
    const cols = [
        "Writer name",
        "Publisher name",
        "Contact",
        "Email",
        "Phone",
        "Source",
        "Consultant",
        "Est. deal value",
        "Stage",
        "Business unit",
        "Assignees",
        "Follow-up",
        "Notes",
    ];
    const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = leads.map((l) => [
        l.name,
        l.company,
        l.contact_name,
        l.contact_email,
        l.contact_phone,
        l.source,
        l.consultant_name,
        l.est_value ?? "",
        stageName(l.stage_id),
        l.business_unit_name,
        (l.assignee_names ?? []).join("; "),
        l.next_follow_up_at ? new Date(l.next_follow_up_at).toISOString() : "",
        l.notes,
    ]
        .map(esc)
        .join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `crm-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}
export default function CrmBoard({ startOpen = false } = {}) {
    const { user } = useAuth();
    const { businessUnits } = useOrgCatalog();
    const canManageStages = user?.role === "admin" || user?.role === "owner";
    const [board, setBoard] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(() => {
        if (startOpen)
            return true; // opened via its own Categories-menu entry
        try {
            return localStorage.getItem(LS_OPEN) === "1";
        }
        catch {
            return false;
        }
    });
    const [editing, setEditing] = useState(null);
    const [flowFor, setFlowFor] = useState(null);
    const [managingStages, setManagingStages] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    // Board (kanban) vs. List (compact table) view — persisted per user.
    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem("crm-view") === "list" ? "list" : "board";
        }
        catch {
            return "board";
        }
    });
    function pickView(v) {
        setView(v);
        try {
            localStorage.setItem("crm-view", v);
        }
        catch {
            /* ignore */
        }
    }
    const [pinned, setPinned] = useState(() => {
        try {
            return localStorage.getItem("crm-pinned") === "1";
        }
        catch {
            return false;
        }
    });
    // Filter / search state — all applied client-side over the loaded leads.
    const [search, setSearch] = useState("");
    const [mineOnly, setMineOnly] = useState(false);
    const [assigneeFilter, setAssigneeFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [buFilter, setBuFilter] = useState("");
    const [minValue, setMinValue] = useState("");
    // Lead the "Add follow-up" modal is open for; consultant rollup modal toggle.
    const [followUpFor, setFollowUpFor] = useState(null);
    const [showConsultants, setShowConsultants] = useState(false);
    // Open straight into full screen when launched from the Quick-access pin —
    // via a localStorage intent (set before this mounts) or a live event (when
    // already mounted).
    useEffect(() => {
        try {
            if (localStorage.getItem("crm-open-intent") === "fullscreen") {
                localStorage.removeItem("crm-open-intent");
                setOpen(true);
                setFullscreen(true);
            }
        }
        catch {
            /* ignore */
        }
        const onOpen = () => {
            try {
                localStorage.removeItem("crm-open-intent");
            }
            catch {
                /* ignore */
            }
            setOpen(true);
            setFullscreen(true);
        };
        window.addEventListener("crm:open-fullscreen", onOpen);
        return () => window.removeEventListener("crm:open-fullscreen", onOpen);
    }, []);
    function togglePinned() {
        setPinned((p) => {
            const next = !p;
            try {
                localStorage.setItem("crm-pinned", next ? "1" : "0");
            }
            catch {
                /* ignore */
            }
            // Tell the Resources page's Quick-access strip to re-read the flag.
            window.dispatchEvent(new Event("crm:pin-changed"));
            return next;
        });
    }
    const load = useCallback(async () => {
        try {
            const b = await api.get("/crm/board");
            setBoard(b);
        }
        catch {
            /* non-fatal — the section just shows the empty/closed state */
        }
        finally {
            setLoaded(true);
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    function toggleOpen() {
        setOpen((o) => {
            const next = !o;
            try {
                localStorage.setItem(LS_OPEN, next ? "1" : "0");
            }
            catch {
                /* ignore */
            }
            return next;
        });
    }
    const stagesById = useMemo(() => {
        const m = new Map();
        for (const s of board?.stages ?? [])
            m.set(s.id, s);
        return m;
    }, [board]);
    // Distinct sources / BUs / assignee names present, for the filter dropdowns.
    const filterOptions = useMemo(() => {
        const sources = new Set();
        const bus = new Set();
        const people = new Set();
        for (const l of board?.leads ?? []) {
            if (l.source)
                sources.add(l.source);
            if (l.business_unit_name)
                bus.add(l.business_unit_name);
            for (const n of l.assignee_names ?? [])
                people.add(n);
            if (l.owner_name)
                people.add(l.owner_name);
        }
        const sort = (s) => Array.from(s).sort((a, b) => a.localeCompare(b));
        return { sources: sort(sources), bus: sort(bus), people: sort(people) };
    }, [board]);
    const anyFilter = !!search.trim() ||
        mineOnly ||
        !!assigneeFilter ||
        !!sourceFilter ||
        !!buFilter ||
        !!minValue;
    // The leads actually shown — board.leads passed through the filter bar.
    const filteredLeads = useMemo(() => {
        const q = search.trim().toLowerCase();
        const min = minValue.trim() ? Number(minValue) : null;
        return (board?.leads ?? []).filter((l) => {
            if (q) {
                const hay = `${l.name} ${l.company ?? ""} ${l.contact_name ?? ""} ${l.contact_email ?? ""}`.toLowerCase();
                if (!hay.includes(q))
                    return false;
            }
            if (mineOnly) {
                const mine = (user?.id && l.owner_id === user.id) ||
                    (user?.id ? l.assignee_ids.includes(user.id) : false);
                if (!mine)
                    return false;
            }
            if (assigneeFilter) {
                const names = [...(l.assignee_names ?? []), l.owner_name ?? ""];
                if (!names.includes(assigneeFilter))
                    return false;
            }
            if (sourceFilter && l.source !== sourceFilter)
                return false;
            if (buFilter && l.business_unit_name !== buFilter)
                return false;
            if (min != null && Number.isFinite(min) && (l.est_value ?? 0) < min)
                return false;
            return true;
        });
    }, [board, search, mineOnly, assigneeFilter, sourceFilter, buFilter, minValue, user?.id]);
    const leadsByStage = useMemo(() => {
        const m = new Map();
        for (const s of board?.stages ?? [])
            m.set(s.id, []);
        for (const l of filteredLeads) {
            if (!m.has(l.stage_id))
                m.set(l.stage_id, []);
            m.get(l.stage_id).push(l);
        }
        return m;
    }, [board, filteredLeads]);
    async function moveLead(lead, stageId) {
        if (stageId === lead.stage_id)
            return;
        try {
            await api.post(`/crm/leads/${lead.id}/move`, { stage_id: stageId });
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't move the lead");
        }
    }
    async function deleteLead(lead) {
        if (!confirm(`Delete lead "${lead.name}"? This can't be undone.`))
            return;
        try {
            await api.del(`/crm/leads/${lead.id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete the lead");
        }
    }
    // Stamp a follow-up date on the lead — a reminder that surfaces in My Day's
    // "Needs follow-up" box. No calendar block is booked.
    async function submitFollowUp(lead, isoDateTime) {
        try {
            await api.post(`/crm/leads/${lead.id}/follow-up`, {
                follow_up_at: isoDateTime,
            });
            setFollowUpFor(null);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't set the follow-up");
        }
    }
    if (!loaded || !board)
        return null;
    const m = board.metrics;
    return (_jsxs("div", { className: "crm-board", children: [_jsxs("button", { type: "button", className: "crm-board-header", onClick: toggleOpen, "aria-expanded": open, children: [_jsx("span", { className: "crm-board-title", children: "\uD83C\uDFAF Lead Pipeline (CRM)" }), _jsxs("span", { className: "crm-board-summary muted small", children: [m.open_leads, " open \u00B7 ", m.signed_quarter, " signed this quarter"] }), _jsx("span", { className: "crm-board-caret", "aria-hidden": "true", children: open ? "▾" : "▸" })] }), (open || fullscreen) && (_jsxs("div", { className: fullscreen ? "crm-fullscreen" : "crm-board-body", children: [fullscreen && (_jsxs("div", { className: "crm-fullscreen-head", children: [_jsx("span", { className: "crm-fullscreen-title", children: "\uD83C\uDFAF Lead Pipeline (CRM)" }), _jsx("button", { type: "button", className: "crm-fullscreen-exit", onClick: () => setFullscreen(false), children: "\u2715 Exit full screen" })] })), _jsxs("div", { className: fullscreen ? "crm-fullscreen-body" : undefined, children: [_jsxs("div", { className: "crm-kpis", children: [_jsxs("div", { className: "crm-kpi", children: [_jsx("span", { className: "crm-kpi-val", children: m.open_leads }), _jsx("span", { className: "crm-kpi-name", children: "Open leads" })] }), _jsxs("div", { className: "crm-kpi", children: [_jsx("span", { className: "crm-kpi-val", children: money(m.pipeline_value) }), _jsx("span", { className: "crm-kpi-name", children: "Open pipeline value" })] }), _jsxs("div", { className: "crm-kpi", children: [_jsx("span", { className: "crm-kpi-val", children: m.signed_quarter }), _jsx("span", { className: "crm-kpi-name", children: "Signed \u00B7 this quarter" })] }), _jsxs("div", { className: "crm-kpi", children: [_jsxs("span", { className: "crm-kpi-val", children: [m.conversion_pct, "%"] }), _jsx("span", { className: "crm-kpi-name", children: "Lead \u2192 signed" })] })] }), _jsxs("p", { className: "muted small crm-okr-note", children: ["\u2728 Signings feed the ", _jsx("strong", { children: "Auto \u00B7 CRM signings" }), " Key-Result source on Goals \u2192 Company OKRs."] }), _jsxs("div", { className: "crm-toolbar", children: [_jsx("button", { type: "button", className: "btn-primary crm-add-btn", onClick: () => setEditing("new"), children: "+ New lead" }), canManageStages && (_jsx("button", { type: "button", className: "crm-stages-btn", onClick: () => setManagingStages(true), children: "\u2699 Manage stages & visibility" })), _jsx("span", { className: "crm-toolbar-spacer" }), _jsxs("div", { className: "crm-view-toggle", role: "tablist", "aria-label": "CRM view", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": view === "board", className: `crm-view-btn ${view === "board" ? "active" : ""}`, onClick: () => pickView("board"), title: "Kanban board", children: "\u25A6 Board" }), _jsx("button", { type: "button", role: "tab", "aria-selected": view === "list", className: `crm-view-btn ${view === "list" ? "active" : ""}`, onClick: () => pickView("list"), title: "Compact list", children: "\u2630 List" })] }), _jsx("button", { type: "button", className: "crm-stages-btn", onClick: () => setShowConsultants(true), title: "Consultant referral summary", children: "\uD83E\uDD1D Consultants" }), _jsx("button", { type: "button", className: "crm-stages-btn", onClick: () => exportLeadsCsv(filteredLeads, (id) => stagesById.get(id)?.name ?? "—"), title: "Export the leads you're viewing to CSV", children: "\u2B07 Export CSV" }), _jsx("button", { type: "button", className: "crm-stages-btn", onClick: togglePinned, title: "Pin the CRM to Quick access", children: pinned ? "📌 Pinned" : "📌 Pin" }), _jsx("button", { type: "button", className: "crm-stages-btn", onClick: () => setFullscreen((f) => !f), children: fullscreen ? "✕ Exit full screen" : "⛶ Full screen" })] }), _jsxs("div", { className: "crm-filters", children: [_jsx("input", { className: "crm-filter-search", value: search, onChange: (e) => setSearch(e.target.value), placeholder: "\uD83D\uDD0D Search name, company, contact\u2026" }), _jsxs("label", { className: "crm-filter-mine", children: [_jsx("input", { type: "checkbox", checked: mineOnly, onChange: (e) => setMineOnly(e.target.checked) }), "My leads"] }), _jsxs("select", { value: assigneeFilter, onChange: (e) => setAssigneeFilter(e.target.value), title: "Filter by assignee / owner", children: [_jsx("option", { value: "", children: "All assignees" }), filterOptions.people.map((n) => (_jsx("option", { value: n, children: n }, n)))] }), _jsxs("select", { value: sourceFilter, onChange: (e) => setSourceFilter(e.target.value), title: "Filter by source", children: [_jsx("option", { value: "", children: "All sources" }), filterOptions.sources.map((s) => (_jsx("option", { value: s, children: s }, s)))] }), _jsxs("select", { value: buFilter, onChange: (e) => setBuFilter(e.target.value), title: "Filter by business unit", children: [_jsx("option", { value: "", children: "All units" }), filterOptions.bus.map((b) => (_jsx("option", { value: b, children: b }, b)))] }), _jsx("input", { className: "crm-filter-min", type: "number", min: "0", value: minValue, onChange: (e) => setMinValue(e.target.value), placeholder: "Min $", title: "Minimum estimated value" }), anyFilter && (_jsx("button", { type: "button", className: "btn-link", onClick: () => {
                                            setSearch("");
                                            setMineOnly(false);
                                            setAssigneeFilter("");
                                            setSourceFilter("");
                                            setBuFilter("");
                                            setMinValue("");
                                        }, children: "Clear" })), _jsxs("span", { className: "crm-filter-count muted small", children: [filteredLeads.length, " of ", board.leads.length] })] }), view === "board" ? (_jsx("div", { className: "crm-columns", children: board.stages.map((s) => {
                                    const leads = leadsByStage.get(s.id) ?? [];
                                    return (_jsxs("div", { className: "crm-col", children: [_jsxs("div", { className: "crm-col-head", children: [_jsx("span", { className: "crm-col-dot", style: { background: s.color || "var(--blueberry)" }, "aria-hidden": "true" }), _jsx("span", { className: "crm-col-name", children: s.name }), _jsx("span", { className: "crm-col-count", children: leads.length })] }), leads.map((l) => (_jsx(LeadCard, { lead: l, stages: board.stages, onMove: (sid) => moveLead(l, sid), onEdit: () => setEditing(l), onDelete: () => deleteLead(l), onStartFlow: () => setFlowFor(l), onFollowUp: () => setFollowUpFor(l) }, l.id))), leads.length === 0 && (_jsx("div", { className: "crm-col-empty muted small", children: "\u2014" }))] }, s.id));
                                }) })) : (_jsx(LeadListView, { leads: filteredLeads, stages: board.stages, onMove: moveLead, onEdit: setEditing, onDelete: deleteLead, onStartFlow: setFlowFor, onFollowUp: setFollowUpFor }))] })] })), editing && (_jsx(LeadModal, { initial: editing === "new" ? null : editing, stages: board.stages, businessUnits: businessUnits, onClose: () => setEditing(null), onSaved: async () => {
                    setEditing(null);
                    await load();
                } })), flowFor && (_jsx(StartFlowModal, { lead: flowFor, stagesById: stagesById, onClose: () => setFlowFor(null), onStarted: async () => {
                    setFlowFor(null);
                    await load();
                } })), managingStages && (_jsx(StagesModal, { stages: board.stages, businessUnits: businessUnits, onClose: () => setManagingStages(false), onChanged: load })), followUpFor && (_jsx(FollowUpModal, { lead: followUpFor, onClose: () => setFollowUpFor(null), onSave: (iso) => submitFollowUp(followUpFor, iso) })), showConsultants && (_jsx(ConsultantsModal, { leads: filteredLeads, stages: board.stages, onClose: () => setShowConsultants(false) }))] }));
}
function LeadCard({ lead, stages, onMove, onEdit, onDelete, onStartFlow, onFollowUp, }) {
    const initials = lead.name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? "")
        .join("");
    return (_jsxs("div", { className: "crm-lead", children: [_jsxs("div", { className: "crm-lead-top", children: [_jsx("span", { className: "crm-lead-av", "aria-hidden": "true", children: initials || "·" }), _jsxs("div", { className: "crm-lead-id", children: [_jsx("div", { className: "crm-lead-name", children: lead.name }), (lead.company || lead.source) && (_jsx("div", { className: "crm-lead-sub muted small", children: [lead.company, lead.source].filter(Boolean).join(" · ") }))] }), lead.est_value != null && (_jsx("span", { className: "crm-lead-val", children: money(lead.est_value) }))] }), _jsxs("div", { className: "crm-lead-meta", children: [lead.business_unit_name && (_jsx("span", { className: "crm-tag bu", children: lead.business_unit_name })), lead.consultant_name && (_jsxs("span", { className: "crm-tag consultant", title: "Referral consultant", children: ["\uD83E\uDD1D ", lead.consultant_name] })), lead.external_source === "artistbeat" && _jsx(ArtistBeatTags, { data: lead.external_data }), (lead.assignee_names && lead.assignee_names.length > 0
                        ? lead.assignee_names
                        : lead.owner_name
                            ? [lead.owner_name]
                            : []).map((n, i) => (_jsx("span", { className: "crm-tag muted", children: n }, `${n}-${i}`)))] }), lead.linked_project_id && (_jsx("a", { className: "crm-lead-proj", href: `/projects?project=${encodeURIComponent(lead.linked_project_id)}`, target: "_blank", rel: "noopener noreferrer", children: "Linked project \u2197" })), lead.next_follow_up_at && (_jsxs("div", { className: `crm-lead-followup small ${isOverdue(lead.next_follow_up_at) ? "overdue" : ""}`, children: ["\uD83D\uDCC5 Follow up ", fmtFollowUp(lead.next_follow_up_at), isOverdue(lead.next_follow_up_at) && " · overdue"] })), lead.can_edit && (_jsxs("div", { className: "crm-lead-actions", children: [_jsx("select", { className: "crm-lead-move", value: lead.stage_id, onChange: (e) => onMove(e.target.value), title: "Move to stage", children: stages.map((s) => (_jsx("option", { value: s.id, children: s.name }, s.id))) }), !lead.linked_project_id && (_jsx("button", { type: "button", className: "btn-link", onClick: onStartFlow, children: "\u25B6 Flow" })), _jsx("button", { type: "button", className: "btn-link", onClick: onFollowUp, children: "\uD83D\uDCC5 Follow up" }), _jsx("button", { type: "button", className: "btn-link", onClick: onEdit, children: "Edit" }), _jsx("button", { type: "button", className: "btn-link", onClick: onDelete, children: "Delete" })] }))] }));
}
// Compact list (table) alternative to the kanban. Same per-lead actions —
// stage move, edit, delete, start-flow — just laid out as rows ordered by
// stage so the whole pipeline reads top-to-bottom at a glance.
function LeadListView({ leads, stages, onMove, onEdit, onDelete, onStartFlow, onFollowUp, }) {
    if (leads.length === 0) {
        return (_jsx("div", { className: "crm-list-empty muted small", children: "No leads yet. Click \u201C+ New lead\u201D to add one." }));
    }
    const stageName = (id) => stages.find((s) => s.id === id)?.name ?? "—";
    // Order by the lead's stage position so the list mirrors the board flow.
    const stageOrder = new Map(stages.map((s, i) => [s.id, s.order_index ?? i]));
    const ordered = [...leads].sort((a, b) => (stageOrder.get(a.stage_id) ?? 0) - (stageOrder.get(b.stage_id) ?? 0));
    return (_jsxs("div", { className: "crm-list", children: [_jsxs("div", { className: "crm-list-head", children: [_jsx("span", { children: "Lead" }), _jsx("span", { children: "Stage" }), _jsx("span", { children: "Value" }), _jsx("span", { children: "Team" }), _jsx("span", { "aria-hidden": "true" })] }), ordered.map((l) => {
                const team = l.assignee_names && l.assignee_names.length > 0
                    ? l.assignee_names.join(", ")
                    : l.owner_name ?? "—";
                return (_jsxs("div", { className: "crm-list-row", children: [_jsxs("div", { className: "crm-list-lead", children: [_jsx("div", { className: "crm-list-name", children: l.name }), (l.company || l.source) && (_jsx("div", { className: "muted small", children: [l.company, l.source].filter(Boolean).join(" · ") })), l.linked_project_id && (_jsx("a", { className: "crm-list-proj small", href: `/projects?project=${encodeURIComponent(l.linked_project_id)}`, target: "_blank", rel: "noopener noreferrer", children: "Linked project \u2197" })), l.next_follow_up_at && (_jsxs("div", { className: `crm-lead-followup small ${isOverdue(l.next_follow_up_at) ? "overdue" : ""}`, children: ["\uD83D\uDCC5 ", fmtFollowUp(l.next_follow_up_at), isOverdue(l.next_follow_up_at) && " · overdue"] }))] }), _jsx("div", { className: "crm-list-stage", children: l.can_edit ? (_jsx("select", { className: "crm-lead-move", value: l.stage_id, onChange: (e) => onMove(l, e.target.value), title: "Move to stage", children: stages.map((s) => (_jsx("option", { value: s.id, children: s.name }, s.id))) })) : (_jsx("span", { className: "crm-tag", children: stageName(l.stage_id) })) }), _jsx("div", { className: "crm-list-val", children: l.est_value != null ? money(l.est_value) : "—" }), _jsxs("div", { className: "crm-list-team muted small", children: [l.business_unit_name && (_jsx("span", { className: "crm-tag bu", children: l.business_unit_name })), _jsx("span", { children: team })] }), _jsx("div", { className: "crm-list-act", children: l.can_edit && (_jsxs(_Fragment, { children: [!l.linked_project_id && (_jsx("button", { type: "button", className: "btn-link", onClick: () => onStartFlow(l), children: "\u25B6 Flow" })), _jsx("button", { type: "button", className: "btn-link", onClick: () => onFollowUp(l), children: "\uD83D\uDCC5" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => onEdit(l), children: "Edit" }), _jsx("button", { type: "button", className: "btn-link", onClick: () => onDelete(l), children: "Delete" })] })) })] }, l.id));
            })] }));
}
function LeadModal({ initial, stages, businessUnits, onClose, onSaved, }) {
    const [name, setName] = useState(initial?.name ?? "");
    const [company, setCompany] = useState(initial?.company ?? "");
    const [contactName, setContactName] = useState(initial?.contact_name ?? "");
    const [contactEmail, setContactEmail] = useState(initial?.contact_email ?? "");
    const [contactPhone, setContactPhone] = useState(initial?.contact_phone ?? "");
    const [source, setSource] = useState(initial?.source ?? "");
    const [consultantName, setConsultantName] = useState(initial?.consultant_name ?? "");
    const [estValue, setEstValue] = useState(initial?.est_value != null ? String(initial.est_value) : "");
    const [buId, setBuId] = useState(initial?.business_unit_id ?? "");
    const [stageId, setStageId] = useState(initial?.stage_id ?? stages[0]?.id ?? "");
    const [notes, setNotes] = useState(initial?.notes ?? "");
    // Assigned teammates — multi-select from the BU-scoped roster.
    const [assigneeIds, setAssigneeIds] = useState(initial?.assignee_ids ?? []);
    const [coworkers, setCoworkers] = useState([]);
    // Import-from-project (create only): existing unlinked projects; picking one
    // prefills the name + BU and links that project to the new lead.
    const [importable, setImportable] = useState([]);
    const [importId, setImportId] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (initial)
            return; // import is a create-time convenience only
        (async () => {
            try {
                setImportable(await api.get("/crm/importable-projects"));
            }
            catch {
                setImportable([]);
            }
        })();
    }, [initial]);
    // Roster for the "Assigned teammates" picker — BU-scoped, includes self.
    useEffect(() => {
        (async () => {
            try {
                setCoworkers(await api.get("/users/coworkers?include_self=true"));
            }
            catch {
                setCoworkers([]);
            }
        })();
    }, []);
    // CRM leads are always tracked under the Sales business unit — default a
    // new lead's BU to Sales (and lock the field below).
    const salesBu = businessUnits.find((b) => b.name.toLowerCase() === "sales");
    useEffect(() => {
        if (!initial && salesBu && !buId)
            setBuId(salesBu.id);
    }, [salesBu, initial, buId]);
    const numOrNull = (s) => {
        const t = s.trim();
        if (!t)
            return null;
        const n = Math.round(Number(t));
        return Number.isFinite(n) && n >= 0 ? n : null;
    };
    async function save() {
        if (!name.trim()) {
            setError("Give the lead a name.");
            return;
        }
        setSaving(true);
        setError(null);
        const payload = {
            name: name.trim(),
            company: company.trim() || null,
            contact_name: contactName.trim() || null,
            contact_email: contactEmail.trim() || null,
            contact_phone: contactPhone.trim() || null,
            source: source.trim() || null,
            consultant_name: consultantName.trim() || null,
            est_value: numOrNull(estValue),
            business_unit_id: buId || null,
            assignee_ids: assigneeIds,
            notes: notes.trim() || null,
        };
        try {
            if (initial) {
                await api.patch(`/crm/leads/${initial.id}`, payload);
                // Stage change goes through /move so the stage-change event is
                // logged (PATCH deliberately doesn't touch the stage).
                if (stageId && stageId !== initial.stage_id) {
                    await api.post(`/crm/leads/${initial.id}/move`, { stage_id: stageId });
                }
            }
            else {
                await api.post("/crm/leads", {
                    ...payload,
                    stage_id: stageId || null,
                    linked_project_id: importId || null,
                });
            }
            onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't save the lead");
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal crm-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: initial ? "Edit lead" : "New lead" }), _jsxs("div", { className: "crm-form-grid", children: [!initial && importable.length > 0 && (_jsxs("label", { className: "crm-form-wide", children: ["(Optional) Add Existing Sales Lead", _jsxs("select", { value: importId, onChange: (e) => {
                                        const id = e.target.value;
                                        setImportId(id);
                                        const p = importable.find((x) => x.id === id);
                                        if (p) {
                                            setName(p.name);
                                            // BU stays Sales — CRM leads are always tracked there.
                                        }
                                    }, children: [_jsx("option", { value: "", children: "\u2014 none (blank lead) \u2014" }), importable.map((p) => (_jsxs("option", { value: p.id, children: [p.name, p.business_unit_name ? ` · ${p.business_unit_name}` : ""] }, p.id)))] })] })), _jsxs("label", { className: "crm-form-wide", children: ["Writer name", _jsx("input", { value: name, onChange: (e) => setName(e.target.value), autoFocus: true })] }), _jsxs("label", { children: ["Publisher name", _jsx("input", { value: company, onChange: (e) => setCompany(e.target.value) })] }), _jsxs("label", { children: ["Source", _jsx("input", { value: source, onChange: (e) => setSource(e.target.value), placeholder: "showcase \u00B7 referral \u00B7 inbound" })] }), _jsxs("label", { children: ["Contact name", _jsx("input", { value: contactName, onChange: (e) => setContactName(e.target.value) })] }), _jsxs("label", { children: ["Contact email", _jsx("input", { value: contactEmail, onChange: (e) => setContactEmail(e.target.value) })] }), _jsxs("label", { children: ["Contact phone", _jsx("input", { value: contactPhone, onChange: (e) => setContactPhone(e.target.value) })] }), _jsxs("label", { children: ["Est. deal value ($)", _jsx("input", { title: "For ArtistBeat audits: the average of the low and high annual estimates", type: "number", min: "0", value: estValue, onChange: (e) => setEstValue(e.target.value) })] }), _jsxs("label", { children: ["Consultant (referrer)", _jsx("input", { value: consultantName, onChange: (e) => setConsultantName(e.target.value), placeholder: "who brought the lead" })] }), !initial && salesBu ? (_jsxs("label", { children: ["Business unit", _jsx("input", { value: "Sales", disabled: true, title: "CRM leads are tracked under Sales" })] })) : (_jsxs("label", { children: ["Business unit", _jsxs("select", { value: buId, onChange: (e) => setBuId(e.target.value), children: [_jsx("option", { value: "", children: "\u2014" }), businessUnits.map((b) => (_jsx("option", { value: b.id, children: b.name }, b.id)))] })] })), _jsxs("label", { children: ["Stage", _jsx("select", { value: stageId, onChange: (e) => setStageId(e.target.value), children: stages.map((s) => (_jsx("option", { value: s.id, children: s.name }, s.id))) })] }), _jsxs("div", { className: "crm-form-wide", children: [_jsx("span", { className: "crm-form-label", children: "Assigned teammates" }), coworkers.length === 0 ? (_jsx("span", { className: "muted small", children: "No teammates available." })) : (_jsx(MultiSelect, { options: coworkers.map((c) => ({ id: c.id, label: c.name })), values: assigneeIds, onChange: setAssigneeIds, placeholder: "Select teammates\u2026" }))] }), _jsxs("label", { className: "crm-form-wide", children: ["Notes", _jsx("textarea", { value: notes, onChange: (e) => setNotes(e.target.value), rows: 2 })] })] }), error && _jsx("div", { className: "error small", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: save, disabled: saving, children: saving ? "Saving…" : initial ? "Save" : "Create lead" })] })] }) }));
}
function StartFlowModal({ lead, stagesById, onClose, onStarted, }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [projName, setProjName] = useState(lead.name);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const ts = await api.get("/project-templates");
                setTemplates(ts);
                // Default to the first pipeline template, else the first template.
                const pipeline = ts.find((t) => t.crm_stage_id);
                setTemplateId((pipeline ?? ts[0])?.id ?? "");
            }
            catch {
                setTemplates([]);
            }
        })();
    }, []);
    const chosen = templates.find((t) => t.id === templateId);
    const advanceTo = chosen?.crm_stage_id ? stagesById.get(chosen.crm_stage_id)?.name : null;
    async function start() {
        if (!templateId) {
            setError("Pick a template flow.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await api.post(`/crm/leads/${lead.id}/start-flow`, {
                template_id: templateId,
                name: projName.trim() || null,
            });
            onStarted();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't start the flow");
            setBusy(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal crm-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("h3", { children: ["Start a flow for ", lead.name] }), _jsx("p", { className: "muted small", children: "Spins up a project from the template, links it to this lead, and advances the stage." }), _jsxs("label", { children: ["Template flow", _jsxs("select", { value: templateId, onChange: (e) => setTemplateId(e.target.value), children: [_jsx("option", { value: "", children: "\u2014" }), templates.map((t) => (_jsxs("option", { value: t.id, children: [t.name, t.crm_stage_id ? " (pipeline)" : ""] }, t.id)))] })] }), _jsxs("label", { children: ["Project name", _jsx("input", { value: projName, onChange: (e) => setProjName(e.target.value) })] }), advanceTo && (_jsxs("p", { className: "muted small", children: ["Will advance the lead to \u201C", advanceTo, "\u201D."] })), error && _jsx("div", { className: "error small", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: busy, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: start, disabled: busy, children: busy ? "Starting…" : "Start flow" })] })] }) }));
}
function StagesModal({ stages, businessUnits, onClose, onChanged, }) {
    const [busy, setBusy] = useState(false);
    const [newName, setNewName] = useState("");
    // CRM visibility: which BUs can see the tracker (empty = everyone).
    const [visBus, setVisBus] = useState(new Set());
    const [visSaving, setVisSaving] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const v = await api.get("/crm/visibility");
                setVisBus(new Set(v.business_unit_ids));
            }
            catch {
                /* ignore */
            }
        })();
    }, []);
    async function toggleVisBu(id) {
        const next = new Set(visBus);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        setVisBus(next);
        setVisSaving(true);
        try {
            await api.put("/crm/visibility", { business_unit_ids: [...next] });
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't save visibility");
        }
        finally {
            setVisSaving(false);
        }
    }
    async function run(fn) {
        setBusy(true);
        try {
            await fn();
            await onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update stages");
        }
        finally {
            setBusy(false);
        }
    }
    const addStage = () => {
        const name = newName.trim();
        if (!name)
            return;
        setNewName("");
        void run(() => api.post("/crm/stages", { name }));
    };
    const rename = (s) => {
        const name = prompt("Stage name", s.name);
        if (name == null)
            return;
        const t = name.trim();
        if (!t || t === s.name)
            return;
        void run(() => api.patch(`/crm/stages/${s.id}`, { name: t }));
    };
    const swap = (a, b) => run(async () => {
        await api.patch(`/crm/stages/${a.id}`, { order_index: b.order_index });
        await api.patch(`/crm/stages/${b.id}`, { order_index: a.order_index });
    });
    const toggleWon = (s) => run(() => api.patch(`/crm/stages/${s.id}`, { is_won: !s.is_won }));
    const toggleLost = (s) => run(() => api.patch(`/crm/stages/${s.id}`, { is_lost: !s.is_lost }));
    const remove = (s) => {
        if (!confirm(`Remove the "${s.name}" stage?`))
            return;
        void run(() => api.del(`/crm/stages/${s.id}`));
    };
    const ordered = [...stages].sort((a, b) => a.order_index - b.order_index);
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal crm-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: "Manage stages & visibility" }), _jsxs("div", { className: "crm-visibility", children: [_jsx("div", { className: "label", children: "Who can see the CRM" }), _jsxs("p", { className: "muted small", style: { marginTop: 0 }, children: ["Leave all unchecked = visible to everyone. Check business units to restrict the CRM to those teams (admins always see it).", visSaving && " · saving…"] }), _jsxs("div", { className: "crm-visibility-bus", children: [businessUnits.map((b) => (_jsxs("label", { className: "crm-visibility-bu", children: [_jsx("input", { type: "checkbox", checked: visBus.has(b.id), onChange: () => toggleVisBu(b.id) }), b.name] }, b.id))), businessUnits.length === 0 && (_jsx("span", { className: "muted small", children: "No business units yet." }))] })] }), _jsx("div", { className: "label", style: { marginTop: "1rem" }, children: "Pipeline stages" }), _jsx("p", { className: "muted small", style: { marginTop: 0 }, children: "Rename, reorder, or flag the won / lost stages. A stage with leads in it can't be removed until those leads are moved." }), _jsx("ul", { className: "crm-stage-list", children: ordered.map((s, i) => (_jsxs("li", { className: "crm-stage-row", children: [_jsx("span", { className: "crm-col-dot", style: { background: s.color || "var(--blueberry)" }, "aria-hidden": "true" }), _jsx("span", { className: "crm-stage-name", children: s.name }), s.is_won && _jsx("span", { className: "crm-tag won", children: "won" }), s.is_lost && _jsx("span", { className: "crm-tag lost", children: "lost" }), _jsxs("span", { className: "crm-stage-tools", children: [_jsx("button", { type: "button", className: "btn-link", disabled: busy || i === 0, onClick: () => swap(s, ordered[i - 1]), title: "Move up", children: "\u2191" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy || i === ordered.length - 1, onClick: () => swap(s, ordered[i + 1]), title: "Move down", children: "\u2193" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy, onClick: () => rename(s), children: "Rename" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy, onClick: () => toggleWon(s), children: s.is_won ? "Unmark won" : "Mark won" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy, onClick: () => toggleLost(s), children: s.is_lost ? "Unmark lost" : "Mark lost" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy, onClick: () => remove(s), children: "Remove" })] })] }, s.id))) }), _jsxs("div", { className: "crm-stage-add", children: [_jsx("input", { value: newName, onChange: (e) => setNewName(e.target.value), placeholder: "New stage name" }), _jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: addStage, children: "+ Add stage" })] }), _jsx("div", { className: "modal-actions", children: _jsx("button", { onClick: onClose, children: "Done" }) })] }) }));
}
// Quick-add a follow-up: stamps a reminder date on the lead (no calendar block).
function FollowUpModal({ lead, onClose, onSave, }) {
    const [date, setDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1); // default: tomorrow
        return d.toISOString().slice(0, 10);
    });
    const [time, setTime] = useState("09:00");
    const [busy, setBusy] = useState(false);
    function save() {
        if (!date)
            return;
        // date + time are local; toISOString converts to the UTC the API stores.
        const dt = new Date(`${date}T${time}`);
        if (Number.isNaN(dt.getTime()))
            return;
        setBusy(true);
        onSave(dt.toISOString());
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal crm-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("h3", { children: ["Follow up: ", lead.name] }), _jsx("p", { className: "muted small", children: "Sets the lead's next follow-up. It's a reminder only \u2014 it shows up in your \"Needs follow-up\" box in My Day (and as the card's tag, flagged when overdue). No calendar block is booked." }), _jsxs("div", { className: "crm-form-grid", children: [_jsxs("label", { children: ["Date", _jsx("input", { type: "date", value: date, onChange: (e) => setDate(e.target.value), autoFocus: true })] }), _jsxs("label", { children: ["Time", _jsx("input", { type: "time", value: time, step: 900, onChange: (e) => setTime(e.target.value) })] })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: busy, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: save, disabled: busy || !date, children: busy ? "Saving…" : "Schedule follow-up" })] })] }) }));
}
// "By consultant" rollup — referred / signed counts + pipeline / signed value,
// computed client-side from the (filtered) leads + the won-stage flags.
function ConsultantsModal({ leads, stages, onClose, }) {
    const rows = useMemo(() => {
        const wonIds = new Set(stages.filter((s) => s.is_won).map((s) => s.id));
        const m = new Map();
        for (const l of leads) {
            const name = l.consultant_name?.trim();
            if (!name)
                continue;
            const r = m.get(name) ?? { name, count: 0, signed: 0, value: 0, signedValue: 0 };
            r.count += 1;
            r.value += l.est_value ?? 0;
            if (wonIds.has(l.stage_id)) {
                r.signed += 1;
                r.signedValue += l.est_value ?? 0;
            }
            m.set(name, r);
        }
        return Array.from(m.values()).sort((a, b) => b.value - a.value);
    }, [leads, stages]);
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal crm-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: "\uD83E\uDD1D Consultant referrals" }), rows.length === 0 ? (_jsx("p", { className: "muted small", children: "No leads have a consultant set yet." })) : (_jsxs("div", { className: "crm-consultants", children: [_jsxs("div", { className: "crm-consultants-head", children: [_jsx("span", { children: "Consultant" }), _jsx("span", { children: "Referred" }), _jsx("span", { children: "Signed" }), _jsx("span", { children: "Pipeline $" }), _jsx("span", { children: "Signed $" })] }), rows.map((r) => (_jsxs("div", { className: "crm-consultants-row", children: [_jsx("span", { className: "crm-consultant-name", children: r.name }), _jsx("span", { children: r.count }), _jsx("span", { children: r.signed }), _jsx("span", { children: money(r.value) }), _jsx("span", { children: money(r.signedValue) })] }, r.name)))] })), _jsx("div", { className: "modal-actions", children: _jsx("button", { onClick: onClose, children: "Done" }) })] }) }));
}
